import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { hardSignout, signout } from 'slices/auth';

const initialState = {
  collection: null,
  status: null,
};

export const refreshAccounts = createAsyncThunk('accounts/pull', async (blobsAPI) => {
  const data = await blobsAPI.all({ types: 'telegram_account' });

  return data;
});

export const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    // Pure reducer: optimistic upsert. The API call lives in the updateAccount thunk.
    accountUpdated: (state, action) => {
      const { id, data, type } = action.payload;

      const account = state.collection?.find((a) => a.id === id);
      if (account) {
        account.data = data;
        account.type = type;
      } else {
        // Stamp an optimistic created_at so the row's time shows immediately; the server's
        // authoritative value replaces it on the next refreshAccounts.
        const created_at = new Date().toISOString();
        state.collection = [{ id, data, type, created_at }, ...(state.collection || [])];
      }
    },
    // Pure reducer: optimistic removal. The API call lives in the deleteAccount thunk.
    accountDeleted: (state, action) => {
      const { id } = action.payload;
      state.collection = state.collection?.filter((a) => a.id !== id) || [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signout.pending, (state) => {
        state.collection = null;
        state.status = null;
      })
      .addCase(hardSignout.pending, (state) => {
        state.collection = null;
        state.status = null;
      })
      .addCase(refreshAccounts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(refreshAccounts.fulfilled, (state, action) => {
        // Newest added first, matching the optimistic prepend in accountUpdated.
        state.collection = [...action.payload].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      });
  },
});

export const { accountUpdated, accountDeleted } = accountsSlice.actions;

export const selectAccounts = (state) => state.accounts.collection;

// Thunks keep side effects (API calls) out of reducers. The call signature is unchanged —
// e.g. dispatch(updateAccount([BlobsAPI, { id, data, type }])) — so existing call-sites keep working.
export const updateAccount =
  ([BlobsAPI, { id, data, type }]) =>
  async (dispatch) => {
    dispatch(accountUpdated({ id, data, type }));
    await BlobsAPI.update({ id, data, type });
    dispatch(refreshAccounts(BlobsAPI));
  };

export const deleteAccount =
  ([BlobsAPI, { id }]) =>
  async (dispatch) => {
    dispatch(accountDeleted({ id }));
    await BlobsAPI.destroy({ id });
    dispatch(refreshAccounts(BlobsAPI));
  };

export default accountsSlice.reducer;
